import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { logSafeErrorWithoutCustomerDataWrapper } from '@atlassian/jira-polaris-lib-errors/src/common/utils/index.tsx';
import { PREMIUM_MODAL_DISMISSED, PREMIUM_LAST_VISIT } from '../../constants.tsx';
import type { Props, State } from '../../types.tsx';

export const loadPremiumModalDismissed =
	(): Action<State, Props, Promise<boolean | undefined>> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		// User property is already loaded
		if (typeof getState().premium.modalDismissed === 'boolean') {
			return;
		}

		try {
			const modalDismissed = await getUserProperty<boolean>(accountId, PREMIUM_MODAL_DISMISSED);

			const state = getState();

			setState({
				premium: {
					...state.premium,
					modalDismissed,
				},
			});
		} catch (error) {
			// Expected error, the property is not set yet
			if (error instanceof FetchError && error.statusCode === 404) {
				const state = getState();

				setState({
					premium: {
						...state.premium,
						modalDismissed: false,
					},
				});

				return;
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.premium.modal-dismissed',
				`Failed to get user property with key: ${PREMIUM_MODAL_DISMISSED}`,
				error instanceof Error ? error : undefined,
			);
		}

		return getState().premium.modalDismissed;
	};

export const setPremiumModalDismissed =
	(): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		const state = getState();

		// we need to pass isPremium to the lastVisit object to ensure that the modal is dismissed
		// when the user is on the premium edition and the last visit was on the free edition
		setState({
			premium: {
				...state.premium,
				modalDismissed: true,
				lastVisit: {
					...state.premium.lastVisit,
					isPremium: true,
				},
			},
		});

		try {
			await setUserProperties(accountId, PREMIUM_MODAL_DISMISSED, 'true');
		} catch (error) {
			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.premium.modal-dismissed',
				`Failed to set user property with key: ${PREMIUM_MODAL_DISMISSED}`,
				error instanceof Error ? error : undefined,
			);
		}
	};

export const loadPremiumLastVisit =
	(isPremiumEdition: boolean): Action<State, Props, Promise<boolean | undefined>> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		// User property is already loaded
		if (typeof getState().premium.lastVisit.isPremium === 'boolean') {
			return;
		}

		try {
			const lastVisitOnPremium = await getUserProperty<boolean>(accountId, PREMIUM_LAST_VISIT);

			const state = getState();

			setState({
				premium: {
					...state.premium,
					lastVisit: {
						...state.premium.lastVisit,
						isPremium: lastVisitOnPremium,
					},
				},
			});
		} catch (error) {
			// Expected error, the property is not set yet
			if (error instanceof FetchError && error.statusCode === 404) {
				const state = getState();

				setState({
					premium: {
						...state.premium,
						lastVisit: {
							...state.premium.lastVisit,
							isPremium: isPremiumEdition,
						},
					},
				});

				return;
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.premium.last-visit',
				`Failed to get user property with key: ${PREMIUM_LAST_VISIT}`,
				error instanceof Error ? error : undefined,
			);
		}

		return getState().premium.modalDismissed;
	};

export const setPremiumLastVisit =
	(isPremium: Boolean): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		if (getState().premium.lastVisit.updated) {
			// If lastVisit has been updated already
			// then don't do it again
			return;
		}

		try {
			await setUserProperties(accountId, PREMIUM_LAST_VISIT, String(isPremium));

			const state = getState();

			setState({
				premium: {
					...state.premium,
					lastVisit: {
						...state.premium.lastVisit,
						updated: true,
					},
				},
			});
		} catch (error) {
			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.premium.last-visit',
				`Failed to set user property with key: ${PREMIUM_LAST_VISIT}`,
				error instanceof Error ? error : undefined,
			);
		}
	};

export const unlockOnboardingFlow =
	(): Action<State, Props> =>
	async ({ getState }) => {
		getState().premium.promise.resolve();
	};
